import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './CoursesMain.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Button from 'react-bootstrap/Button';
import { useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { Table } from 'react-bootstrap';
import './Pagination.css';
import './CourseReport.css';
import axiosInstance from '../axios.config';
import { Pagination } from '@mui/material';  // Added Pagination from MUI

const Reports = () => {
  // Memoize lngsltd to prevent infinite loops
  const lngsltd = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('languageSelected')) || {};
    } catch (error) {
      console.error('Error parsing language data:', error);
      return {};
    }
  }, []);

  const [progressData, setProgressData] = useState([]);
  const [storeLocations, setStoreLocations] = useState({});
  const [filterText, setFilterText] = useState('');
  const [storeOptions, setStoreOptions] = useState([]);
  const [dynamicText, setDynamicText] = useState(lngsltd["Loading...Please wait"] || "Loading...Please wait");

  //For filters option
  const [search, setSearch] = useState('');
  const [filteredData, setFilteredData] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(10); // Adjusted to display 5 groups per page

  const paginate = (event, pageNumber) => setCurrentPage(pageNumber);

  const { id } = useParams();

  // Memoize fetch functions to prevent unnecessary re-renders
  const fetchStoreData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/Store`);
      const storeLocationMap = {};
      const options = response.data.map(store => ({
        id: store.storeID,
        name: store.storeLocation
      }));
      response.data.forEach(store => {
        storeLocationMap[store.storeID] = store.storeLocation;
      });
      setStoreLocations(storeLocationMap);
      setStoreOptions(options);
    } catch (error) {
      toast.info(lngsltd['There was an issue fetching the store data'] || 'There was an issue fetching the store data');
    }
  }, [lngsltd]);

  const fetchCourseSteps = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/UserCourseStep/GetAllCoursesProgress`);
      setProgressData(response.data || []);
      if (!response.data || response.data.length === 0) {
        setTimeout(() => {
          setDynamicText(lngsltd["No Data Found"] || "No Data Found");
        }, 2000);
      }
    } catch (error) {
      toast.info(lngsltd['There was an issue fetching the course steps'] || 'There was an issue fetching the course steps');
      setProgressData([]);
    }
  }, [lngsltd]);

  useEffect(() => {
    fetchStoreData();
    fetchCourseSteps();
  }, [id, fetchStoreData, fetchCourseSteps]);

  const handleClearFilter = () => {
    setFilterText('');
  };

  const [filteredProgressData, setFilteredProgressData] = useState([]);

  // Memoize text strings to prevent unnecessary re-renders
  const noDataText = useMemo(() => lngsltd["No Data Found"] || "No data found", [lngsltd]);
  const loadingText = useMemo(() => lngsltd["Loading...Please wait"] || "Loading...Please wait", [lngsltd]);

  useEffect(() => {
    if (progressData.length !== 0) {
      const filteredData = progressData.filter(progress => {
        const storeLocation = storeLocations[progress.storeID] || '';
        return (
          storeLocation.toLowerCase().includes(filterText.toLowerCase()) ||
          progress.userName?.toLowerCase().includes(filterText.toLowerCase()) ||
          progress.courseName?.toLowerCase().includes(filterText.toLowerCase()) ||
          progress.progressPercentage?.toString().toLowerCase().includes(filterText.toLowerCase())
        );
      });
      setFilteredProgressData(filteredData);
      setCurrentPage(1); // Reset to first page when filter changes
      if (filteredData.length === 0) {
        setDynamicText(noDataText);
      } else {
        setDynamicText(loadingText);
      }
    } else {
      setFilteredProgressData([]);
    }
  }, [filterText, progressData, storeLocations, noDataText, loadingText]);

  // Pagination logic
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredProgressData.slice(indexOfFirstCourse, indexOfLastCourse);



  return (
    <div className="reportpageContainer reportdiv w-100 d-flex flex-column bg-light m-3">
      <h1 className="reportpageTitle text-center">
        {lngsltd["Reports"]?.toUpperCase() || "REPORTS"}
      </h1>

      <div className="report-content-wrapper">
        <div className="report-filters-section">
          <div className="d-flex align-items-center mb-3">
            <div className="search-wrapper">
              <input
                type="text"
                className="form-control search-input-plain"
                placeholder={lngsltd["Search"]}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              className="ms-2"
              onClick={handleClearFilter}
            >
              {lngsltd["Clear Filter"] || "Clear Filter"}
            </Button>
          </div>
        </div>

        <div className="table-wrapper">
          <Table striped bordered hover className="report-table-grid">
            <thead>
              <tr>
                <th className="text-center">{lngsltd["Store"]}</th>
                <th className="text-center">{lngsltd["User"]}</th>
                <th className="text-center">{lngsltd["Course"]}</th>
                <th className="text-center">{lngsltd["Progress"]}</th>
              </tr>
            </thead>
            <tbody>
              {currentCourses && currentCourses.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-4">
                    {dynamicText}
                  </td>
                </tr>
              ) : (
                currentCourses.map((progress, index) => (
                  <tr key={index}>
                    <td className="text-center">{storeLocations[progress.storeID] || 'N/A'}</td>
                    <td className="text-center">{progress.userName}</td>
                    <td className="text-center">{progress.courseName}</td>
                    <td className="text-center">
                      <div className="progress-display">
                        <div className="progress-text">{progress.progressPercentage}%</div>
                        <div className="progress-bar-wrapper">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${progress.progressPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {filteredProgressData.length > coursesPerPage && (
          <div className="pagination-section">
            <Pagination
              count={Math.ceil(filteredProgressData.length / coursesPerPage)}
              page={currentPage}
              onChange={paginate}
              siblingCount={1}
              boundaryCount={1}
              color="primary"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
